/**
 * Media demand planning for multi-track playback (pure, testable).
 *
 * The preview keeps a pool of <video> elements. With one element per SOURCE,
 * the same source active on two tracks at once can only honor ONE media time
 * — the other track shows the wrong frame. planMedia() enumerates per-CLIP
 * demands; assignElements() maps each demanding clip to a stable element key
 * `${sourceId}#${n}`, cloning extra elements only when a source is demanded
 * more than once at the same instant.
 */
import { audioClipGainAt, clipAudioGainAt } from './compositor';
import { clipDuration, clipEnd } from '../format/helpers';
import type { Edit } from '../format/types';

export interface MediaDemand {
  sourceId: string;
  clipId: string;
  mediaTime: number;
  rate: number;
  gain: number;
}

/** All per-clip media demands at time t (video-track + audio-track clips). */
export function planMedia(edit: Edit, t: number): MediaDemand[] {
  const out: MediaDemand[] = [];
  edit.tracks.forEach((track, trackIndex) => {
    if (track.kind === 'video') {
      for (const clip of track.clips) {
        const dur = clipDuration(clip);
        if (t >= clip.start && t < clip.start + dur) {
          out.push({
            sourceId: clip.source,
            clipId: clip.id,
            mediaTime: clip.in + (t - clip.start) * clip.speed,
            rate: clip.speed,
            gain: clipAudioGainAt(edit, trackIndex, clip, t),
          });
        }
      }
    } else if (track.kind === 'audio') {
      for (const clip of track.clips) {
        if (t >= clip.start && t < clipEnd(clip)) {
          const rate = clip.speed ?? 1;
          out.push({
            sourceId: clip.source,
            clipId: clip.id,
            mediaTime: clip.in + (t - clip.start) * rate,
            rate,
            gain: audioClipGainAt(edit, trackIndex, clip.id, t),
          });
        }
      }
    }
  });
  return out;
}

export function elementKey(sourceId: string, n: number): string {
  return `${sourceId}#${n}`;
}

const CLIP_PREFIX = 'clip:';

/**
 * Element key for a CACHE-BACKED clip. Cached clips play from their own small
 * remuxed file, so they get a dedicated element keyed by clip identity rather
 * than sharing the per-source element (which can only hold one position).
 */
export function clipElementKey(clipId: string): string {
  return `${CLIP_PREFIX}${clipId}#0`;
}

export type ParsedElementKey =
  | { kind: 'source'; sourceId: string; n: number }
  | { kind: 'clip'; clipId: string };

/**
 * Inverse of elementKey/clipElementKey. Splits on the LAST '#'.
 *
 * Returns a discriminated result rather than always a {sourceId, n}: a clip key
 * parsed as a source key yields a bogus sourceId and `n: NaN`, which silently
 * misbehaves at every call site (the element gets disposed as an unknown source,
 * or swept as a clone because `NaN === 0` is false).
 */
export function parseElementKey(key: string): ParsedElementKey {
  if (key.startsWith(CLIP_PREFIX)) {
    const rest = key.slice(CLIP_PREFIX.length);
    const i = rest.lastIndexOf('#');
    return { kind: 'clip', clipId: i >= 0 ? rest.slice(0, i) : rest };
  }
  const i = key.lastIndexOf('#');
  return { kind: 'source', sourceId: key.slice(0, i), n: Number(key.slice(i + 1)) };
}

/**
 * Assign each demanding clip a pool-element key. Assignments are STABLE: a
 * clip keeps its element from `prev` while it stays active (no mid-clip
 * element swap = no decode hiccup); new demands take the lowest free index
 * for their source (#0 first, so the single-source common case keeps using
 * the sole preloaded element).
 */
export function assignElements(
  demands: MediaDemand[],
  prev: Map<string, string>,
  /** Cache-backed clips: returns their dedicated element key, else undefined. */
  cachedKeyFor?: (clipId: string) => string | undefined,
): Map<string, string> {
  const next = new Map<string, string>();
  const taken = new Set<string>();

  // A cached clip always uses its own element. The key is unique per clip, so
  // it can never contend with another demand.
  if (cachedKeyFor) {
    for (const d of demands) {
      const key = cachedKeyFor(d.clipId);
      if (key) {
        next.set(d.clipId, key);
        taken.add(key);
      }
    }
  }

  for (const d of demands) {
    if (next.has(d.clipId)) continue;
    const kept = prev.get(d.clipId);
    // Must check the key IS a source key for this source. A `startsWith`
    // test on `${sourceId}#` rejects a clip key outright, which would make a
    // cached clip flip between its cache element and a source element every
    // frame. Conversely a STALE clip key (entry evicted since) must be
    // rejected here so the clip falls through to a streaming element.
    const parsed = kept ? parseElementKey(kept) : null;
    if (
      kept &&
      parsed?.kind === 'source' &&
      parsed.sourceId === d.sourceId &&
      !taken.has(kept)
    ) {
      next.set(d.clipId, kept);
      taken.add(kept);
    }
  }
  for (const d of demands) {
    if (next.has(d.clipId)) continue;
    for (let n = 0; ; n++) {
      const key = elementKey(d.sourceId, n);
      if (!taken.has(key)) {
        next.set(d.clipId, key);
        taken.add(key);
        break;
      }
    }
  }
  return next;
}

/** Highest per-source element count a plan needs (for pool sizing/warnings). */
export function maxConcurrentPerSource(
  demands: MediaDemand[],
  /** Cache-backed clips don't contend for source elements — exclude them. */
  cachedKeyFor?: (clipId: string) => string | undefined,
): number {
  const counts = new Map<string, number>();
  let max = 0;
  for (const d of demands) {
    if (cachedKeyFor?.(d.clipId)) continue;
    const c = (counts.get(d.sourceId) ?? 0) + 1;
    counts.set(d.sourceId, c);
    max = Math.max(max, c);
  }
  return max;
}
